import { useEffect, useRef, useState } from "react";

const GATES = [
  { label: "TERRITORY",    val: "NA-WEST",   color: "#00F0FF" },
  { label: "ACCOUNT TIER", val: "TIER 1",    color: "#007AFF" },
  { label: "CAPACITY",     val: "AVAILABLE", color: "#FFB800" },
  { label: "SIGNAL TYPE",  val: "MATCHED",   color: "#D946EF" },
];

export default function RoutingVisual() {
  const [step, setStep] = useState(-1);
  const tRef = useRef(null);

  useEffect(() => {
    const cycle = () => {
      setStep(-1);
      const delays = [300, 900, 1500, 2100, 2700, 3500];
      delays.forEach((d, i) => {
        setTimeout(() => setStep(i), d);
      });
      tRef.current = setTimeout(cycle, 5400);
    };
    tRef.current = setTimeout(cycle, 300);
    return () => clearTimeout(tRef.current);
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center" data-testid="routing-visual">
      <svg viewBox="0 0 320 400" className="w-full h-full max-h-[480px]">
        {/* Trunk */}
        <line x1="160" y1="30" x2="160" y2="376" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="2 3" />
        <line
          x1="160" y1="30"
          x2="160"
          y2={30 + (step >= 0 ? Math.min((step + 1), 6) * 58 : 0)}
          stroke="#CCFF00"
          strokeWidth="1.4"
          style={{ transition: "y2 0.5s ease" }}
        />

        {/* INPUT */}
        <rect
          x="40" y="12" width="240" height="40"
          fill="#0d0d0d"
          stroke={step >= 0 ? "#CCFF00" : "#2a2a2a"}
          strokeWidth="1"
          style={{ transition: "all 0.3s" }}
        />
        <text
          x="54" y="30"
          fill={step >= 0 ? "#CCFF00" : "#6a6a6a"}
          fontFamily="IBM Plex Mono"
          fontSize="10"
          letterSpacing="2.5"
          style={{ transition: "all 0.3s" }}
        >
          INCOMING SIGNAL
        </text>
        <text
          x="54" y="44"
          fill={step >= 0 ? "#FFB800" : "#3a3a3a"}
          fontFamily="IBM Plex Mono"
          fontSize="10"
        >
          FUNDING · TIER 1
        </text>
        <circle
          cx="160" cy="52" r="3"
          fill={step >= 0 ? "#CCFF00" : "#333"}
          style={{ transition: "all 0.3s" }}
        />

        {/* GATES */}
        {GATES.map((g, i) => {
          const y = 80 + i * 58;
          const passed = step > i;
          const active = step === i + 1;
          const on = passed || active;
          return (
            <g key={g.label}>
              <rect
                x="30" y={y} width="260" height="40"
                fill={on ? "#0d0d0d" : "#0a0a0a"}
                stroke={on ? g.color : "#1f1f1f"}
                strokeWidth="1"
                style={{ transition: "all 0.3s" }}
              />
              <text
                x="44" y={y + 17}
                fill={on ? g.color : "#565656"}
                fontFamily="IBM Plex Mono"
                fontSize="10"
                letterSpacing="2"
                style={{ transition: "all 0.3s" }}
              >
                {g.label}
              </text>
              <text
                x="44" y={y + 32}
                fill={on ? "#ffffff" : "#2a2a2a"}
                fontFamily="Azeret Mono"
                fontSize="11"
                fontWeight="bold"
                style={{ transition: "all 0.3s" }}
              >
                {on ? `✓ ${g.val}` : "—"}
              </text>
              <circle
                cx="272" cy={y + 20} r="4"
                fill={passed ? g.color : "transparent"}
                stroke={active ? g.color : passed ? g.color : "#2f2f2f"}
                strokeWidth="1.2"
                style={{ transition: "all 0.3s" }}
              >
                {active && <animate attributeName="r" values="3;5.5;3" dur="0.8s" repeatCount="indefinite" />}
              </circle>
              <circle
                cx="160" cy={y + 40} r="2.5"
                fill={passed ? "#CCFF00" : "#222"}
                style={{ transition: "all 0.3s" }}
              />
            </g>
          );
        })}

        {/* OUTPUT */}
        <rect
          x="30" y="332" width="260" height="46"
          fill="#0d0d0d"
          stroke={step >= 5 ? "#CCFF00" : "#1f1f1f"}
          strokeWidth="1"
          style={{ transition: "all 0.3s" }}
        />
        <text
          x="44" y="352"
          fill={step >= 5 ? "#CCFF00" : "#5a5a5a"}
          fontFamily="IBM Plex Mono"
          fontSize="10"
          letterSpacing="2"
          style={{ transition: "all 0.3s" }}
        >
          {step >= 5 ? "✓ REP ASSIGNED" : "REP ASSIGNMENT"}
        </text>
        <text
          x="44" y="368"
          fill={step >= 5 ? "#FFFFFF" : "#2a2a2a"}
          fontFamily="Azeret Mono"
          fontSize="11"
          fontWeight="bold"
        >
          {step >= 5 ? "Brief delivered · 212ms" : "Pending routing..."}
        </text>

        {/* Traveling dot */}
        {step >= 0 && step <= 5 && (
          <circle r="4.5" fill="#CCFF00" style={{ filter: "drop-shadow(0 0 6px #CCFF00)" }}>
            <animate
              attributeName="cy"
              from={52 + Math.max(0, step) * 58}
              to={52 + Math.min(6, step + 1) * 58}
              dur="0.5s"
              fill="freeze"
            />
            <animate attributeName="cx" values="160" dur="0.5s" fill="freeze" />
            <animate attributeName="opacity" values="1;1;0" keyTimes="0;0.85;1" dur="0.5s" fill="freeze" />
          </circle>
        )}
      </svg>
    </div>
  );
}
